import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  ColumnDatas,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  Modal,
  Table,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { DocInfo, PermissionPolicy } from "../../types/files";
import utilsCommon from "../../shared/utils/common";
import utilsDocs from "../../shared/utils/docs";
import usePolicyStore from "../../redux/dispatcher/usePolicyStore";
import { Policy } from "../../types/workgroup";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import PolicyToolTip from "../PolicyToolTip";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import SetPolicyPage from "../SePolicyPage";
import { nanoid } from "@reduxjs/toolkit";
import { Trans } from "react-i18next";
import WorkgroupAddModal from "../WorkgroupAddModal";
import { t } from "i18next";
import dayjs from "dayjs";

interface ChangeFileAuthModalProps {
  open: boolean;
  docInfo?: DocInfo; // 권한 수정하고 싶은 파일
  onClose: () => void;
  onClickAfterSubmit?: () => void; // 적용 버튼 클릭 후, 액션
}
const ChangeFileAuthModal: React.FC<ChangeFileAuthModalProps> = ({
  open,
  docInfo,
  onClose,
  onClickAfterSubmit,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";

  /** Hook 정리 */
  const toast = useToast();
  const checkboxGroup = useCheckboxGroup<string>();
  const permissionListNew = useRef([] as ColumnDatas[]);
  const { setFilePolicy } = usePolicyStore();
  const { documentState, getContentDetail } = useDocumentStore();

  /** State 정리 */
  const [key, setKey] = useState(0); // table 업데이트용
  const [savedPermissionList, changeSavedPermissionList] = useState<Policy[]>(
    []
  ); // 저장된 권한 리스트
  const [tempPermissionList, changeTempPermissionList] = useState<Policy[]>([]); // 수정된 권한 리스트 => 저장된 권한과 같은 지 체크하기 위해 필요
  const [addRecipientsModal, toggleAddRecipientsModal] = useState(false); // 수신자 추가 모달 열고 닫기
  const [targetSettingUserAuth, changeTargetSettingUserAuth] =
    useState<Policy>(); // 특정 사용자의 권한 수정시 사용. targetValue 가 ALL 일 경우엔 체크박스한 사용자들을 모두 사용가능

  /** UseEffect 정리 */
  useEffect(() => {
    const permission =
      documentState?.contentDetail?.permissionPolicyList.filter(
        (policy: Policy) => policy.uniqueId !== "MASTER"
      );
    changeSavedPermissionList(permission);
    changeTempPermissionList(permission);
  }, [
    documentState?.contentDetail?.permissionPolicyList,
    docInfo,
    documentState?.contentDetail,
  ]);
  useEffect(() => {
    if (!tempPermissionList) return;
    let newDataContent = [] as ColumnDatas[];

    tempPermissionList.forEach((item) =>
      newDataContent.push(getTableData(item))
    );
    permissionListNew.current = newDataContent;
    setKey(key + 1);
  }, [tempPermissionList, checkboxGroup.selected]);
  useEffect(() => {
    if (!docInfo) return;
    getContentDetail({
      syncId: docInfo?.syncId,
      workgroupId: docInfo.workgroup.workgroupId,
    });
  }, [docInfo]);

  /** 함수 정리 */
  /** 권한 수정시, 사용 => temp만 수정됨 */
  const handleDoAction = (
    key: string,
    targetUserId?: string,
    indPolicy?: PermissionPolicy
  ) => {
    let nextPermission = tempPermissionList;
    const targetUserIds = targetUserId
      ? [targetUserId]
      : checkboxGroup.selected;
    setKey((key1) => key1 + 1);

    if (indPolicy) {
      if (targetUserId === "ALL") {
        nextPermission = tempPermissionList.map((item) => {
          return {
            ...item,
            changeCode: "UPDATE",
            uniqueId: item.uniqueId,
            targetValue: item.targetValue,
            policy:
              item.targetValue && checkboxGroup.isSelected(item.targetValue)
                ? indPolicy
                : item.policy,
            updateUser: loginDto.userInfo.userId,
          };
        });
      } else {
        nextPermission = [
          ...(tempPermissionList.filter(
            (item: Policy) =>
              item.targetValue && !targetUserIds.includes(item.targetValue)
          ) ?? []),
          ...targetUserIds?.map((item) => {
            const savedPermission =
              tempPermissionList.filter(
                (item1: Policy) =>
                  item1.targetValue && item === item1.targetValue
              ) ?? [];
            return {
              ...savedPermission[0],
              changeCode: "CREATE",
              uniqueId: null,
              workgroupCode: docInfo?.workgroup.workgroupId,
              syncId: docInfo?.syncId,
              targetType: "USER",
              targetValue: item,
              authType: "LOGIN",
              policyType: "DOCUMENT",
              policy: indPolicy,
              updateUser: loginDto.userInfo.userId,
            };
          }),
        ];
      }
      changeTargetSettingUserAuth(undefined);
    } else {
      changeTargetSettingUserAuth(
        tempPermissionList.filter(
          (item) =>
            item.targetValue &&
            item.authType !== "DENY" &&
            targetUserIds.includes(item.targetValue) &&
            targetUserIds.length === 1
        )[0] ?? {
          changeCode: "CREATE",
          uniqueId: null,
          workgroupCode: docInfo?.workgroup.workgroupId,
          syncId: docInfo?.syncId,
          targetType: "USER",
          targetValue: "ALL",
          authType: "LOGIN",
          policyType: "DOCUMENT",
          policy:
            utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
              .workgroupPermissionDTO?.policy,
          updateUser: loginDto.userInfo.userId,
        }
      );
      return;
    }

    changeTempPermissionList([...nextPermission]);
  };
  /** 권한 저장 => temp, save 둘다 수정됨 */
  const handleSavePermission = () => {
    if (!docInfo?.syncId) return;
    const existUserList =
      savedPermissionList?.map((item: Policy) => item.targetValue) ?? [];
    let newNextPermission = [] as Policy[];
    newNextPermission = newNextPermission.concat(
      tempPermissionList
        .filter((item) => !existUserList.includes(item.targetValue))
        .map((item) => {
          return { ...item, changeCode: "CREATE" };
        })
    );
    newNextPermission = newNextPermission.concat(
      tempPermissionList
        .filter(
          (item) =>
            item.changeCode !== null && existUserList.includes(item.targetValue)
        )
        .map((item: Policy) => {
          return {
            ...item,
            changeCode: "UPDATE",
            uniqueId: savedPermissionList.filter(
              (item1: Policy) => item.targetValue === item1.targetValue
            )[0].uniqueId,
            policy: item.policy
              ? {
                  ...item.policy,
                  fileShareLinkPolicy: null,
                  notificationPolicy: null,
                }
              : undefined,
          };
        })
    );
    setFilePolicy({
      syncId: docInfo?.syncId,
      workgroupId: docInfo.workgroup.workgroupId,
      setPolicyDTO: newNextPermission,
    }).then(() =>
      getContentDetail({
        syncId: docInfo?.syncId,
        workgroupId: docInfo.workgroup.workgroupId,
      })
    );
  };
  /** 사용자 추가 */
  const handleAddExtraReceipt = async (
    targetUserList?: {
      email?: string | null;
      status: number;
      userId: string;
      userName?: string | null;
      userType: number;
      policy?: PermissionPolicy;
    }[]
  ) => {
    if (!docInfo?.syncId || !targetUserList) return;
    const newUsers = targetUserList.map((item) => {
      return {
        authType: "LOGIN",
        changeCode: "CREATE",
        createUser: myId,
        policyCode: null,
        policyType: "DOCUMENT",
        syncId: docInfo.syncId,
        targetType: "USER",
        targetValue: item.userId,
        policy: item.policy,
        uniqueId: null,
        updateTime: 1,
        email: item.email,
        userName: item?.userName ?item?.userName : "",
        status: item.status,
        userType: item.userType,
        updateUser: myId,
        workgroupCode: "NONEWORKGROUP",
      };
    });
    setFilePolicy({
      syncId: docInfo?.syncId,
      workgroupId: docInfo.workgroup.workgroupId,
      setPolicyDTO: newUsers,
    }).then(() => {
      toast.toastMsg(nanoid(), t("file.alert.addRecipetSuccess"), "success");
      getContentDetail({
        syncId: docInfo?.syncId,
        workgroupId: docInfo.workgroup.workgroupId,
      });
    });
  };
  /** 사용자 제거 => 즉시 제거됨 */
  const removeUserPermission = async (
    targetUserList: (string | undefined)[]
  ) => {
    if (!docInfo?.syncId) return;
    const targetPolicy = savedPermissionList.filter((perm) =>
      targetUserList.includes(perm.targetValue)
    );
    let resp = await setFilePolicy({
      syncId: docInfo?.syncId,
      workgroupId: docInfo.workgroup.workgroupId,
      setPolicyDTO: targetPolicy
        .filter((user) => user.uniqueId)
        .map((item) => {
          return { ...item, changeCode: "DELETE" };
        }),
    });
    if (resp.payload) {
      toast.toastMsg(nanoid(), t("sendingFile.alert.removeSuccess"), "success");
      if (
        targetUserList[0] &&
        targetUserList.length === 1 &&
        checkboxGroup.isSelected(targetUserList[0])
      )
        checkboxGroup.handleClick(targetUserList[0]);
      getContentDetail({
        syncId: docInfo?.syncId,
        workgroupId: docInfo.workgroup.workgroupId,
      });
    }
  };
  /** UI: 사용자 드롭다운 */
  const renderUserDropdown = (
    targetAuth: "MASTER" | "PERSONAL" | "WORKGROUP" | "NONE",
    targetType?: Policy
  ) => {
    let workgroupPolicy = savedPermissionList?.filter(
      (item: Policy) => item.targetType === "WORKGROUP"
    )[0];
    const saveMyPolicy = savedPermissionList?.filter(
      (item: Policy) =>
        targetType && item.targetValue === targetType.targetValue
    )[0];
    return (
      <Box classes="mg ml-auto" width={"32px"}>
        <Dropdown
          positionStrategy="fixed"
          align="left"
          dropdownMaxWidth={180}
          buttonWidth={180}
          placeholder="  "
          btnStyle="table-icon"
          leadingIcon={<Icons variant={"line"} label={"moreVertical"} />}
        >
          <PolicyToolTip
            showTooltip
            policy={
              !!targetType
                ? JSON.stringify(targetType) === JSON.stringify(saveMyPolicy)
                  ? targetType
                  : {
                      ...targetType,
                      policy: {
                        ...targetType.policy,
                        allowViewCount: targetType.policy?.allowViewCount ?? 0,
                        currentViewCount: 0,
                      },
                    }
                : workgroupPolicy
            }
            buttonClick={key}
            targetAuth={targetAuth}
            pageType={"myBox"}
            docInfo={docInfo}
          >
            <DropdownItem
              label={t("common.setAuth")}
              onClick={() =>
                targetType && handleDoAction("PERSONAL", targetType.targetValue)
              }
              frontIcon={<Icons variant="line" label="setting" />}
            />
          </PolicyToolTip>
          <DropdownItem
            label={t("workgroup.action.delete")}
            onClick={() => {
              removeUserPermission([targetType?.targetValue]);
            }}
            frontIcon={<Icons variant="line" label="delete" />}
          />
        </Dropdown>
      </Box>
    );
  };
  /** UI: 수신자 테이블 */
  const getTableData = (row: Policy) => {
    const userId = row?.targetValue ?? "";
    let targetType = tempPermissionList?.filter(
      (item: Policy) => item.targetValue === row?.targetValue
    );

    const targetAuth =
      !targetType || targetType?.length === 0
        ? "WORKGROUP"
        : targetType[0].uniqueId === "MASTER"
        ? "MASTER"
        : targetType[0].authType === "DENY"
        ? "NONE"
        : "PERSONAL";
    return {
      data: [
        {
          columnText: (
            <Box alignItems="center">
              <Avatar
                size={"32"}
                icon={row?.userName ? undefined : "user"}
                uniqueCode={utilsCommon.getUserColor(row.userType, row.status, row?.userName)}
                name={row.userName}
              />
              <Box
                direction="column"
                classes="mg ml-8"
                justifyContent="center"
                width={"calc(100% - 40px)"}
              >
                <Box alignItems="center">
                  <Tooltip
                    message={utilsCommon.getUserNameIdTag(userId, row.userName)}
                  >
                    <Typography ellipsis type="body2_rg">
                      {utilsCommon.getUserNameIdTag(userId, row.userName)}
                    </Typography>
                  </Tooltip>
                  {row.status && row.status !== 1 && (
                    <Typography
                      exactColor={getColor("COLOR_TEXT_ERROR")}
                      classes="unregist"
                      type="caption1_rg"
                      whiteSpace="noWrap"
                    >
                      {t("common.noRegist")}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  message={utilsCommon.getUserPositionNDept({
                    deptName: row.deptName,
                    companyName: row.companyName,
                    userType: row.userType,
                  })}
                >
                  <Typography
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    type="caption1_rg"
                    ellipsis
                  >
                    {utilsCommon.getUserPositionNDept({
                      deptName: row.deptName,
                      companyName: row.companyName,
                      userType: row.userType,
                    })}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          ),
        },
        {
          columnText: renderUserDropdown(targetAuth, targetType[0]),
        },
      ],
      checkBoxClick: () => checkboxGroup.handleClick(userId),
      id: userId,
    } as ColumnDatas;
  };
  /** UI: 개별 또는 체크박스한 사용자들의 권한 수정 모달 */
  const renderUpdatePersonalAuthModal = () => {
    return (
      <Modal
        width={540}
        bodyHeight={556}
        open={!!targetSettingUserAuth}
        onClose={() => changeTargetSettingUserAuth(undefined)}
        title={
          <Box alignItems="center">
            {targetSettingUserAuth?.targetValue !== "ALL" && "‘"}
            {targetSettingUserAuth &&
              targetSettingUserAuth?.targetValue !== "ALL" && (
                <Tooltip
                  message={
                    targetSettingUserAuth.userName ??
                    targetSettingUserAuth.targetValue
                  }
                >
                  <Typography ellipsis maxWidth={272} type="h2">
                    {targetSettingUserAuth.userName ??
                      targetSettingUserAuth.targetValue}
                  </Typography>
                </Tooltip>
              )}
            {targetSettingUserAuth?.targetValue !== "ALL"
              ? "’ " + t("common.setAuth")
              : t("sendingFile.setAuth.changeAllTitle")}
          </Box>
        }
        isBack
        onBack={() => changeTargetSettingUserAuth(undefined)}
        footer={
          <Box>
            <Button
              btnStyle="fill"
              label={t("common.cancel")}
              type={"secondary"}
              onClick={() => changeTargetSettingUserAuth(undefined)}
            />

            <Button
              btnStyle="fill"
              label={t("common.apply")}
              state={
                targetSettingUserAuth?.policy?.allowDays !== undefined &&
                targetSettingUserAuth?.policy?.allowDays > 0 &&
                dayjs(
                  targetSettingUserAuth &&
                    targetSettingUserAuth.uniqueId &&
                    targetSettingUserAuth.targetType === "USER"
                    ? dayjs(targetSettingUserAuth.policy?.startTime)
                        .set("hour", 0)
                        .valueOf()
                    : docInfo?.createTime
                )
                  .add(targetSettingUserAuth?.policy?.allowDays, "d")
                  .isBefore(dayjs().startOf("day"), "day")
                  ? "disable"
                  : "default"
              }
              onClick={() => {
                handleDoAction(
                  "PERSONAL",
                  targetSettingUserAuth?.targetValue ?? "ALL",
                  targetSettingUserAuth?.policy
                );
              }}
            />
          </Box>
        }
        body={
          <SetPolicyPage
            targetSettingUserAuth={targetSettingUserAuth}
            docInfo={docInfo}
            policy={targetSettingUserAuth?.policy}
            isInd
            page={"SEND_FILE"}
            changePolicy={(newPolicy) => {
              targetSettingUserAuth &&
                changeTargetSettingUserAuth({
                  ...targetSettingUserAuth,
                  policy: newPolicy,
                });
            }}
          />
        }
      />
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      width={540}
      isCustom
      classes="fileAuth"
    >
      {addRecipientsModal && (
        <WorkgroupAddModal
          createType={"ADD_RECIPIENTS_SEND_FILE"}
          open={addRecipientsModal}
          disableValues={[
            ...tempPermissionList.map((item) => item.targetValue ?? ""),
            myId,
          ]}
          onSubmitAfter={(
            users?: {
              email?: string | null;
              status: number;
              userId: string;
              userName?: string | null;
              userType: number;
              policy?: PermissionPolicy;
            }[]
          ) => handleAddExtraReceipt(users)}
          onClose={() => toggleAddRecipientsModal(false)}
        />
      )}
      {!!targetSettingUserAuth && renderUpdatePersonalAuthModal()}
      <Box alignItems="center" height={90} classes="fileAuth-header">
        {utilsDocs.renderDocIcon(docInfo?.contentName, 40)}
        <Box
          classes="mg ml-8"
          direction="column"
          maxWidth={"calc(100% - 80px)"}
        >
          <Tooltip message={docInfo?.contentName}>
            <Typography ellipsis type="subtitle1">
              {docInfo?.contentName}
            </Typography>
          </Tooltip>

          <Typography
            type="body4_rg"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {`${utilsDocs.fileSizeFormat(
              documentState?.contentDetail?.documentInfo?.fileSize
            )} • ${utilsDocs.fileFormattoStr(docInfo?.contentName)}`}
          </Typography>
        </Box>
        <Box onClick={onClose} classes="cursor pointer mg mt-4 ml-auto">
          <Icons variant="line" label="closeLarge" />
        </Box>
      </Box>
      <Box height={422} direction="column">
        <Box
          height={30}
          classes="pd pl-28 pr-28"
          width={"100%"}
          boxSizing={"border-box"}
        >
          <Typography type="subtitle2">
            {checkboxGroup.selected.length === 0 ? (
              <Trans
                i18nKey={"sendingFile.recipientsWithCount"}
                values={{ count: tempPermissionList?.length ?? 0 }}
                components={{
                  1: (
                    <Typography
                      type="subtitle2"
                      exactColor={getColor("COLOR_TEXT_BRAND")}
                    />
                  ),
                }}
              />
            ) : (
              <Trans
                i18nKey="common.chosenMember"
                values={{
                  count: checkboxGroup.selected?.length,
                }}
                components={{
                  1: (
                    <Typography
                      type="subtitle2"
                      exactColor={getColor("COLOR_TEXT_BRAND")}
                    />
                  ),
                }}
              />
            )}
          </Typography>
          {checkboxGroup.selected.length > 0 && (
            <Box classes="mg ml-auto">
              <Button
                label={t("common.setAuth")}
                btnStyle="link"
                leadingIcon={<Icons variant="line" label="shield" isOn />}
                onClick={() => handleDoAction("PERSONAL")}
              />

              <Button
                label={t("workgroup.action.delete")}
                btnStyle="link"
                size="lg"
                type="error"
                state={
                  checkboxGroup.selected.length === 0 ? "disable" : "default"
                }
                onClick={() => {
                  removeUserPermission(checkboxGroup.selected);
                  checkboxGroup.handleAllClick([]);
                }}
                leadingIcon={
                  <Icons
                    variant="line"
                    label="delete"
                    stroke={getColor("COLOR_ICON_ERROR")}
                  />
                }
              />
            </Box>
          )}
        </Box>
        <Table
          width="calc(100% - 56px)"
          clickIds={checkboxGroup.selected}
          checkBoxClick={() =>
            checkboxGroup.handleAllClick(
              checkboxGroup.selected.length === tempPermissionList?.length
                ? []
                : tempPermissionList.map((item) => item.targetValue ?? "") ?? []
            )
          }
          totalCount={tempPermissionList?.length}
          height="345px"
          classes="fileAuth-table pd pl-28 pr-28 mg mt-4"
          columnHeight="md"
          header={[
            {
              headerText: t("common.idAndName"),
              width: "calc(100% - 80px)",
            },
            {
              headerText: "",
              width: "40px",
            },
          ]}
          data={permissionListNew.current}
          endReached={() => console.log("NOT_REQUIRED")}
        />
      </Box>
      <Box height={88} alignItems="center">
        <Button
          btnStyle="link"
          label={t("sendingFile.addUser")}
          leadingIcon={<Icons variant="line" label="addPerson" />}
          classes="mg ml-20"
          onClick={() => toggleAddRecipientsModal(true)}
        />
        <Box classes="mg ml-auto mr-28">
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            type="secondary"
            onClick={onClose}
          />
          <Button
            btnStyle="fill"
            label={t("common.save")}
            classes="mg ml-8"
            state={
              JSON.stringify(savedPermissionList) ===
              JSON.stringify(tempPermissionList)
                ? "disable"
                : "default"
            }
            onClick={() => {
              handleSavePermission();
              onClickAfterSubmit && onClickAfterSubmit();
              checkboxGroup.handleAllClick([]);
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};
export default ChangeFileAuthModal;
